import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Eye,
  Save,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Bold,
  Italic,
  Underline,
  Download,
  Upload,
  Variable,
  Maximize2,
  Lock,
  Copy,
  Star,
  Pen,
  ChevronDown,
  ChevronUp,
  ChevronsDown,
  ChevronsUp,
  Settings,
  Minimize2,
  Grid3x3,
  Shield,
  ArrowLeft,
  Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { PreApprovalTemplate, PreApprovalData } from '@/types/pre-approval';
import { KeyboardShortcuts } from './KeyboardShortcuts';
import { downloadVisualPreApprovalPDF } from '@/utils/pre-approval/visualTemplatePdfGenerator';
import { preApprovalTemplateService } from '@/services/preApprovalTemplateService';
import { userPreferencesService } from '@/services/userPreferencesService';
import { TemplateEditorSidebar } from './TemplateEditorSidebar';
import { ZoomControls } from './ZoomControls';

import logger from '@/utils/logger';
interface TemplateElement {
  id: string;
  type: 'text' | 'image' | 'divider' | 'signature';
  content?: string; // For text elements, can include {{variables}}
  imageUrl?: string; // For image elements
  x: number;
  y: number;
  width: number;
  height: number;
  styles: {
    fontSize: number;
    fontFamily: string;
    fontWeight: 'normal' | 'bold';
    fontStyle: 'normal' | 'italic';
    textDecoration: 'none' | 'underline';
    textAlign: 'left' | 'center' | 'right';
    color: string;
    backgroundColor?: string;
    padding: number;
    borderRadius: number;
    border?: string;
    lineHeight?: number;
  };
}

interface TemplateLayout {
  id: string;
  name: string;
  pageSize: 'letter' | 'a4';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  elements: TemplateElement[];
  createdAt: string;
  updatedAt: string;
}

interface ResizeHandle {
  position: 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
  cursor: string;
}

const resizeHandles: ResizeHandle[] = [
  { position: 'nw', cursor: 'nw-resize' },
  { position: 'n', cursor: 'n-resize' },
  { position: 'ne', cursor: 'ne-resize' },
  { position: 'e', cursor: 'e-resize' },
  { position: 'se', cursor: 'se-resize' },
  { position: 's', cursor: 's-resize' },
  { position: 'sw', cursor: 'sw-resize' },
  { position: 'w', cursor: 'w-resize' }
];

// Collision detection utilities
const checkElementsOverlap = (
  elem1: { x: number; y: number; width: number; height: number },
  elem2: { x: number; y: number; width: number; height: number }
): boolean => {
  return !(
    elem1.x + elem1.width <= elem2.x ||
    elem2.x + elem2.width <= elem1.x ||
    elem1.y + elem1.height <= elem2.y ||
    elem2.y + elem2.height <= elem1.y
  );
};

const findValidPosition = (
  element: TemplateElement,
  allElements: TemplateElement[],
  proposedX: number,
  proposedY: number
): { x: number; y: number; hasCollision: boolean } => {
  const testElement = { ...element, x: proposedX, y: proposedY };
  const otherElements = allElements.filter(el => el.id !== element.id);
  
  const hasCollision = otherElements.some(other => checkElementsOverlap(testElement, other));
  
  if (!hasCollision) {
    return { x: proposedX, y: proposedY, hasCollision: false };
  }
  
  // If collision, return original position
  return { x: element.x, y: element.y, hasCollision: true };
};

// Layer reorder utilities — array position = visual layer (last = on top)
const bringToFront = (elements: TemplateElement[], elementId: string): TemplateElement[] => {
  const idx = elements.findIndex(el => el.id === elementId);
  if (idx === -1 || idx === elements.length - 1) {
return elements;
}
  return [...elements.slice(0, idx), ...elements.slice(idx + 1), elements[idx]];
};

const sendToBack = (elements: TemplateElement[], elementId: string): TemplateElement[] => {
  const idx = elements.findIndex(el => el.id === elementId);
  if (idx === -1 || idx === 0) {
return elements;
}
  return [elements[idx], ...elements.slice(0, idx), ...elements.slice(idx + 1)];
};

const bringForward = (elements: TemplateElement[], elementId: string): TemplateElement[] => {
  const idx = elements.findIndex(el => el.id === elementId);
  if (idx === -1 || idx === elements.length - 1) {
return elements;
}
  const result = [...elements];
  [result[idx], result[idx + 1]] = [result[idx + 1], result[idx]];
  return result;
};

const sendBackward = (elements: TemplateElement[], elementId: string): TemplateElement[] => {
  const idx = elements.findIndex(el => el.id === elementId);
  if (idx === -1 || idx === 0) {
return elements;
}
  const result = [...elements];
  [result[idx - 1], result[idx]] = [result[idx], result[idx - 1]];
  return result;
};

const defaultElement: Omit<TemplateElement, 'id' | 'x' | 'y'> = {
  type: 'text',
  content: 'Click to edit text',
  width: 200,
  height: 40,
  styles: {
    fontSize: 14,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'left',
    color: '#000000',
    padding: 8,
    borderRadius: 0,
    lineHeight: 1.5
  }
};

const variableCategories = {
  Borrowers: [
    { id: 'borrowerGreeting', label: 'Borrower Greeting (Smart)' },
    { id: 'borrowerNames', label: 'All Borrower Names (incl. Co-Borrower)' },
    { id: 'borrowerFirstName', label: 'Borrower First Name' },
    { id: 'borrowerLastName', label: 'Borrower Last Name' },
    { id: 'borrowerEmail', label: 'Borrower Email' },
    { id: 'borrowerPhone', label: 'Borrower Phone Number' },
    { id: 'coBorrowerGreeting', label: 'Co-Borrower Greeting (Smart)' },
    { id: 'coBorrowerFirstName', label: 'Co-Borrower First Name' },
    { id: 'coBorrowerLastName', label: 'Co-Borrower Last Name' },
    { id: 'coBorrowerEmail', label: 'Co-Borrower Email' },
    { id: 'coBorrowerPhone', label: 'Co-Borrower Phone Number' }
  ],
  'Loan Information': [
    { id: 'loanAmount', label: 'Pre-approved Loan Amount' },
    { id: 'purchasePrice', label: 'Purchase Price' },
    { id: 'loanType', label: 'Loan Type' },
    { id: 'downPaymentAmount', label: 'Downpayment $' },
    { id: 'downPaymentPercent', label: 'Downpayment %' },
    { id: 'ltv', label: 'LTV' },
    { id: 'propertyType', label: 'Property Type' },
    { id: 'propertyOccupancy', label: 'Property Occupancy' },
    { id: 'propertyStreet', label: 'Property Street' },
    { id: 'propertyCity', label: 'Property City' },
    { id: 'propertyState', label: 'Property State' },
    { id: 'propertyZipCode', label: 'Property Zip Code' }
  ],
  'Company Information': [
    { id: 'companyName', label: 'Company Name' },
    { id: 'companyNMLS', label: 'Company NMLS' },
    { id: 'companyEmail', label: 'Company Email' },
    { id: 'companyWebsite', label: 'Company Website' },
    { id: 'companyPhone', label: 'Company Phone Number' }
  ],
  'Loan Officer Information': [
    { id: 'loanOfficerName', label: 'Loan Officer Name' },
    { id: 'loanOfficerNMLS', label: 'Loan Officer NMLS' },
    { id: 'loanOfficerEmail', label: 'Loan Officer Email' },
    { id: 'loanOfficerPhone', label: 'Loan Officer Phone Number' },
    { id: 'loanOfficerMobile', label: 'Loan Officer Mobile Number' },
    { id: 'loanOfficerWebsite', label: 'Loan Officer Website' },
    { id: 'loanOfficerSignature', label: 'Loan Officer Signature' }
  ],
  Other: [
    { id: 'dateOfPreApproval', label: 'Date of Pre-approval' },
    { id: 'expirationDate', label: 'Expiration Date' }
  ]
};

const zoomLevels = [25, 50, 75, 100, 125, 150, 200];

interface VisualTemplateBuilderProps {
  initialTemplate?: TemplateLayout;
  onTemplateSelect?: (template: PreApprovalTemplate) => void;
  onBackToGallery?: () => void;
}

export const VisualTemplateBuilder: React.FC<VisualTemplateBuilderProps> = ({ initialTemplate, onTemplateSelect, onBackToGallery }) => {
  const [template, setTemplate] = useState<TemplateLayout>(initialTemplate || {
    id: `template-${Date.now()}`,
    name: 'New Pre-Approval Template',
    pageSize: 'letter',
    orientation: 'portrait',
    margins: { top: 72, bottom: 72, left: 72, right: 72 },
    elements: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [editingElement, setEditingElement] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isInteractingWithToolbar, setIsInteractingWithToolbar] = useState(false);
  const [isVariablePopoverOpen, setIsVariablePopoverOpen] = useState(false);
  const [preventOverlap, setPreventOverlap] = useState(true);
  const [showGrid, setShowGrid] = useState(false);
  const [zoom, setZoom] = useState(100); // Zoom percentage
  const [isSaving, setIsSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Initialize templateData with current template info
  const [templateData, setTemplateData] = useState<PreApprovalTemplate | null>(() => {
    if (initialTemplate && !initialTemplate.id.startsWith('template-')) {
      // For existing templates, create initial templateData
      return {
        id: initialTemplate.id,
        name: initialTemplate.name,
        is_corporate_default: false,
        is_locked: false
      } as PreApprovalTemplate;
    }
    return null;
  });
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isMyPersonalDefault, setIsMyPersonalDefault] = useState(false);
  const [isCheckingDefault, setIsCheckingDefault] = useState(false);

  const [contextMenu, setContextMenu] = useState<{
    elementId: string;
    x: number;
    y: number;
  } | null>(null);

  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    dragType: 'move' | 'resize' | null;
    dragOffset: { x: number; y: number };
    resizeHandle?: string;
    initialSize?: { width: number; height: number };
    initialPos?: { x: number; y: number };
  }>({ isDragging: false, dragType: null, dragOffset: { x: 0, y: 0 } });
  
  const [history, setHistory] = useState<TemplateLayout[]>([initialTemplate || template]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  // Canvas dimensions based on page size
  const canvasDimensions = {
    letter: { width: 612, height: 792 }, // 8.5" x 11" at 72 DPI
    a4: { width: 595, height: 842 } // A4 at 72 DPI
  }[template.pageSize];

  const addToHistory = useCallback((newTemplate: TemplateLayout) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newTemplate);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const updateTemplate = useCallback((updates: Partial<TemplateLayout>) => {
    const newTemplate = {
      ...template,
      ...updates,
      updatedAt: new Date().toISOString()
    };
    setTemplate(newTemplate);
    addToHistory(newTemplate);
  }, [template, addToHistory]);

  const addElement = useCallback((type: TemplateElement['type']) => {
    const newElement: TemplateElement = {
      ...defaultElement,
      id: `element-${Date.now()}`,
      type,
      x: template.margins.left + 50,
      y: template.margins.top + 50,
      ...(type === 'divider' && { 
        content: undefined,
        width: canvasDimensions.width - template.margins.left - template.margins.right,
        height: 2,
        styles: { ...defaultElement.styles, backgroundColor: '#000000' }
      }),
      ...(type === 'image' && {
        content: undefined,
        width: 150,
        height: 150,
        styles: { ...defaultElement.styles, backgroundColor: '#f3f4f6' }
      }),
      ...(type === 'signature' && {
        content: '{{loanOfficerSignature}}',
        width: 250,  // Increased from 200 for longer names
        height: 60,
        styles: {
          ...defaultElement.styles,
          border: '2px dashed #999',
          backgroundColor: '#f9f9f9',
          textAlign: 'center' as const
        }
      })
    };

    updateTemplate({
      elements: [...template.elements, newElement]
    });
    setSelectedElement(newElement.id);
  }, [template, canvasDimensions, updateTemplate]);

  const updateElement = useCallback((elementId: string, updates: Partial<TemplateElement>) => {
    const newElements = template.elements.map(el =>
      el.id === elementId ? { ...el, ...updates } : el
    );
    updateTemplate({ elements: newElements });
  }, [template.elements, updateTemplate]);

  const deleteElement = useCallback((elementId: string) => {
    const newElements = template.elements.filter(el => el.id !== elementId);
    updateTemplate({ elements: newElements });
    if (selectedElement === elementId) {
      setSelectedElement(null);
    }
  }, [template.elements, selectedElement, updateTemplate]);

  // Direct text editing
  const handleDoubleClick = useCallback((e: React.MouseEvent, elementId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const element = template.elements.find(el => el.id === elementId);
    if (!element || element.type !== 'text') {
return;
}
    
    setEditingElement(elementId);
    setEditingText(element.content || '');
    
    // Focus the input after render
    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 0);
  }, [template.elements]);

  const saveEditingText = useCallback(() => {
    if (editingElement) {
      updateElement(editingElement, { content: editingText });
      setEditingElement(null);
      setEditingText('');
      setIsInteractingWithToolbar(false);
    }
  }, [editingElement, editingText, updateElement]);

  const cancelEditingText = useCallback(() => {
    setEditingElement(null);
    setEditingText('');
    setIsInteractingWithToolbar(false);
  }, []);

  // Variable insertion
  const insertVariable = useCallback((variable: string) => {
    if (!editingElement) {
return;
}
    
    const cursorPos = editInputRef.current?.selectionStart || editingText.length;
    const newText = 
      `${editingText.slice(0, cursorPos)  
      }{{${variable}}}${  
      editingText.slice(cursorPos)}`;
    
    setEditingText(newText);
    
    // Restore focus and cursor position
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
        const newCursorPos = cursorPos + variable.length + 4; // +4 for {{}}
        editInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [editingElement, editingText]);

  // Image upload
  const handleImageUpload = useCallback((elementId: string) => {
    setSelectedElement(elementId);
    fileInputRef.current?.click();
  }, []);

  const processImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedElement) {
return;
}

    // Validate file type for PDF compatibility
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!supportedTypes.includes(file.type.toLowerCase())) {
      toast.error(`Unsupported file format: ${file.type}. Please use JPEG, PNG, GIF, or WEBP files for PDF compatibility.`);
      
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Please use images smaller than 5MB.');
      
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target?.result as string;
      updateElement(selectedElement, { imageUrl });
      toast.success(`Image uploaded successfully (${file.type})`);
    };
    
    reader.onerror = () => {
      toast.error('Failed to read image file. Please try a different image.');
    };
    
    reader.readAsDataURL(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedElement, updateElement]);

  // Mouse handlers for dragging and resizing
  const handleMouseDown = useCallback((e: React.MouseEvent, elementId: string, resizeHandle?: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const element = template.elements.find(el => el.id === elementId);
    if (!element) {
return;
}

    setSelectedElement(elementId);
    
    if (resizeHandle) {
      setDragState({
        isDragging: true,
        dragType: 'resize',
        dragOffset: { x: e.clientX, y: e.clientY },
        resizeHandle,
        initialSize: { width: element.width, height: element.height },
        initialPos: { x: element.x, y: element.y }
      });
    } else {
      setDragState({
        isDragging: true,
        dragType: 'move',
        dragOffset: {
          x: e.clientX - element.x,
          y: e.clientY - element.y
        }
      });
    }
  }, [template.elements]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragState.isDragging || !selectedElement) {
return;
}

    const element = template.elements.find(el => el.id === selectedElement);
    if (!element) {
return;
}

    if (dragState.dragType === 'move') {
      const newX = e.clientX - dragState.dragOffset.x;
      const newY = e.clientY - dragState.dragOffset.y;

      // Constrain to canvas bounds
      const constrainedX = Math.max(template.margins.left, 
        Math.min(newX, canvasDimensions.width - template.margins.right - element.width));
      const constrainedY = Math.max(template.margins.top,
        Math.min(newY, canvasDimensions.height - template.margins.bottom - element.height));

      if (preventOverlap) {
        // Check for collisions
        const validPosition = findValidPosition(element, template.elements, constrainedX, constrainedY);
        if (!validPosition.hasCollision) {
          updateElement(selectedElement, { x: validPosition.x, y: validPosition.y });
        }
      } else {
        updateElement(selectedElement, { x: constrainedX, y: constrainedY });
      }
    } else if (dragState.dragType === 'resize' && dragState.resizeHandle) {
      const deltaX = e.clientX - dragState.dragOffset.x;
      const deltaY = e.clientY - dragState.dragOffset.y;
      const initialSize = dragState.initialSize!;
      const initialPos = dragState.initialPos!;
      
      let newWidth = initialSize.width;
      let newHeight = initialSize.height;
      let newX = element.x;
      let newY = element.y;

      // Handle resize based on handle position
      switch (dragState.resizeHandle) {
        case 'e':
          newWidth = Math.max(50, initialSize.width + deltaX);
          break;
        case 'w':
          newWidth = Math.max(50, initialSize.width - deltaX);
          newX = Math.min(initialPos.x + deltaX, initialPos.x + initialSize.width - 50);
          break;
        case 's':
          newHeight = Math.max(30, initialSize.height + deltaY);
          break;
        case 'n':
          newHeight = Math.max(30, initialSize.height - deltaY);
          newY = Math.min(initialPos.y + deltaY, initialPos.y + initialSize.height - 30);
          break;
        case 'se':
          newWidth = Math.max(50, initialSize.width + deltaX);
          newHeight = Math.max(30, initialSize.height + deltaY);
          break;
        case 'sw':
          newWidth = Math.max(50, initialSize.width - deltaX);
          newHeight = Math.max(30, initialSize.height + deltaY);
          newX = Math.min(initialPos.x + deltaX, initialPos.x + initialSize.width - 50);
          break;
        case 'ne':
          newWidth = Math.max(50, initialSize.width + deltaX);
          newHeight = Math.max(30, initialSize.height - deltaY);
          newY = Math.min(initialPos.y + deltaY, initialPos.y + initialSize.height - 30);
          break;
        case 'nw':
          newWidth = Math.max(50, initialSize.width - deltaX);
          newHeight = Math.max(30, initialSize.height - deltaY);
          newX = Math.min(initialPos.x + deltaX, initialPos.x + initialSize.width - 50);
          newY = Math.min(initialPos.y + deltaY, initialPos.y + initialSize.height - 30);
          break;
      }

      updateElement(selectedElement, { 
        width: newWidth, 
        height: newHeight,
        x: newX,
        y: newY
      });
    }
  }, [dragState, selectedElement, template, canvasDimensions, updateElement, preventOverlap]);

  const handleMouseUp = useCallback(() => {
    setDragState({ isDragging: false, dragType: null, dragOffset: { x: 0, y: 0 } });
  }, []);

  // Global mouse events
  useEffect(() => {
    if (dragState.isDragging) {
      const handleGlobalMouseUp = () => handleMouseUp();
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [dragState.isDragging, handleMouseUp]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      setSelectedElement(null);
      if (editingElement && !isInteractingWithToolbar) {
        saveEditingText();
      }
    }
  }, [editingElement, isInteractingWithToolbar, saveEditingText]);

  // History management
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setTemplate(history[historyIndex - 1]);
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setTemplate(history[historyIndex + 1]);
    }
  }, [history, historyIndex]);

  // Zoom management
  const zoomIn = useCallback(() => {
    const currentIndex = zoomLevels.indexOf(zoom);
    if (currentIndex < zoomLevels.length - 1) {
      setZoom(zoomLevels[currentIndex + 1]);
    }
  }, [zoom]);

  const zoomOut = useCallback(() => {
    const currentIndex = zoomLevels.indexOf(zoom);
    if (currentIndex > 0) {
      setZoom(zoomLevels[currentIndex - 1]);
    }
  }, [zoom]);

  const zoomToFit = useCallback(() => {
    // Reset to 100% - in the future, could calculate optimal zoom to fit viewport
    setZoom(100);
  }, []);

  const canZoomIn = zoom < 200;
  const canZoomOut = zoom > 25;

  // Save template
  const saveTemplate = useCallback(async () => {
    if (isSaving) {
return;
}
    
    setIsSaving(true);
    try {
      // Save directly to database without localStorage backup
      const dbTemplate = {
        id: template.id.startsWith('template-') ? undefined : template.id, // New templates don't have DB IDs yet
        name: template.name,
        description: `Visual template - ${template.pageSize} ${template.orientation}`,
        sections: [], // Visual templates don't use sections
        style_config: {
          pageSize: template.pageSize,
          orientation: template.orientation,
          margins: template.margins,
          elements: template.elements
        },
        is_locked: templateData?.is_locked ?? false,
        is_corporate_default: templateData?.is_corporate_default ?? false
      };
      
      // templateData values are already used above, no need to override
      
      const { data, error } = await preApprovalTemplateService.saveTemplate(dbTemplate);
      
      if (error) {
        toast.error(`Failed to save template: ${error.message}`);
        logger.error('Error saving template:', error);
      } else {
        toast.success('Template saved successfully');
        
        // Update the template ID and data if this was a new template
        if (data) {
          if (template.id.startsWith('template-')) {
            setTemplate({
              ...template,
              id: data.id || template.id
            });
          }
          // Update templateData with the fresh data from database
          // This ensures UI reflects what was actually saved
          setTemplateData(data);
        }
        
        // Convert to PreApprovalTemplate format if callback exists
        if (onTemplateSelect && data) {
          const preApprovalTemplate: PreApprovalTemplate = {
            id: data.id || template.id,
            name: template.name,
            loanOfficerId: 'current-user', // This should come from auth context
            sections: [], // Visual templates don't use sections
            visualLayout: template, // Store the visual layout
            createdAt: template.createdAt,
            updatedAt: template.updatedAt
          };
          onTemplateSelect(preApprovalTemplate);
        }
      }
    } catch (error) {
      logger.error('Error saving template:', error);
      toast.error('Failed to save template. It has been saved locally as a backup.');
    } finally {
      setIsSaving(false);
    }
  }, [template, onTemplateSelect, isSaving, templateData]);

  // Duplicate template
  const duplicateTemplate = useCallback(async () => {
    if (!templateData?.id) {
return;
}
    
    setIsSaving(true);
    try {
      const { data, error } = await preApprovalTemplateService.duplicateTemplate(
        templateData.id,
        `${template.name} (Copy)`
      );
      
      if (error) {
        toast.error(`Failed to duplicate template: ${error.message}`);
      } else if (data) {
        toast.success('Template duplicated successfully');
        
        // Update the current template with the new duplicate's data
        setTemplate({
          ...template,
          id: data.id || `template-${Date.now()}`,
          name: data.name
        });
        setTemplateData(data);
        
        // Notify parent component if needed
        if (onTemplateSelect) {
          const preApprovalTemplate: PreApprovalTemplate = {
            id: data.id || template.id,
            name: data.name,
            loanOfficerId: 'current-user',
            sections: [],
            visualLayout: template,
            createdAt: template.createdAt,
            updatedAt: template.updatedAt
          };
          onTemplateSelect(preApprovalTemplate);
        }
      }
    } catch (error) {
      logger.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    } finally {
      setIsSaving(false);
    }
  }, [template, templateData, onTemplateSelect]);

  // Check if current user is admin and load template data
  useEffect(() => {
    const initializeTemplate = async () => {
      // Check if user is admin
      const isUserAdmin = await preApprovalTemplateService.isCurrentUserAdmin();
      setIsAdmin(isUserAdmin);
      
      // If we have an initial template with an ID, load its data from the database
      if (initialTemplate?.id && !initialTemplate.id.startsWith('template-')) {
        const { data, error } = await preApprovalTemplateService.getTemplateById(initialTemplate.id);
        if (data && !error) {
          setTemplateData(data);
        }
      }
    };
    initializeTemplate();
  }, [initialTemplate]);
  
  // Load template data on initial mount
  useEffect(() => {
    const loadTemplateData = async () => {
      // Load template data for existing templates
      if (template.id && !template.id.startsWith('template-')) {
        const { data, error } = await preApprovalTemplateService.getTemplateById(template.id);
        if (data && !error) {
          // Only update if we don't have templateData yet, or if database has newer data
          setTemplateData(prevData => {
            if (!prevData) {
              // First load - use database data
              return data;
            } else {
              // Subsequent loads - preserve user changes unless database timestamp is newer
              const dbIsNewer = data.updated_at && prevData.updated_at &&
                                new Date(data.updated_at) > new Date(prevData.updated_at);

              if (dbIsNewer) {
                return data;
              } else {
                return prevData; // Keep user's local changes
              }
            }
          });
        }
      }
    };

    // Only load if we don't have templateData yet
    if (!templateData) {
      loadTemplateData();
    }
  }, [template.id, templateData]); // Include dependencies

  // Check if this template is user's personal default
  useEffect(() => {
    const checkPersonalDefault = async () => {
      if (!template.id || template.id.startsWith('template-')) {
        return;
      }

      setIsCheckingDefault(true);
      try {
        const { data: userPrefs } = await userPreferencesService.getPreferences();
        const isDefault = userPrefs?.workflowPreferences?.defaultPreApprovalTemplateId === template.id;
        setIsMyPersonalDefault(isDefault);
      } catch (error) {
        logger.error('Error checking personal default:', error);
      } finally {
        setIsCheckingDefault(false);
      }
    };

    checkPersonalDefault();
  }, [template.id]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Set as personal default: Ctrl/Cmd + Shift + D
      if (e.key === 'd' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault();
        if (template.id && !template.id.startsWith('template-')) {
          setPersonalDefault();
        } else {
          toast.error('Please save the template before setting it as default');
        }
        return;
      }

      // Delete selected element
      if (e.key === 'Delete' && selectedElement && !editingElement) {
        e.preventDefault();
        deleteElement(selectedElement);
      }
      
      // Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        undo();
      }
      
      // Redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
      
      // Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveTemplate();
      }

      // Zoom in
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        zoomIn();
      }

      // Zoom out
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        zoomOut();
      }

      // Reset zoom to 100%
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        zoomToFit();
      }

      // Layer ordering shortcuts (only when element is selected and not editing text)
      // Note: Shift+[ = '{' and Shift+] = '}' on standard keyboards, so check both
      if (selectedElement && !editingElement && (e.ctrlKey || e.metaKey) && (e.key === ']' || e.key === '[' || e.key === '}' || e.key === '{')) {
        e.preventDefault();
        const isForward = e.key === ']' || e.key === '}';
        const isBack = e.key === '[' || e.key === '{';
        let newElements = template.elements;
        if (e.shiftKey && isForward) {
          newElements = bringToFront(template.elements, selectedElement);
        } else if (!e.shiftKey && isForward) {
          newElements = bringForward(template.elements, selectedElement);
        } else if (!e.shiftKey && isBack) {
          newElements = sendBackward(template.elements, selectedElement);
        } else if (e.shiftKey && isBack) {
          newElements = sendToBack(template.elements, selectedElement);
        }
        if (newElements !== template.elements) {
          updateTemplate({ elements: newElements });
        }
        return;
      }

      // Arrow keys for fine positioning
      if (selectedElement && !editingElement && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const element = template.elements.find(el => el.id === selectedElement);
        if (!element) {
return;
}
        
        const step = e.shiftKey ? 10 : 1;
        let newX = element.x;
        let newY = element.y;
        
        switch (e.key) {
          case 'ArrowUp':
            newY = Math.max(template.margins.top, element.y - step);
            break;
          case 'ArrowDown':
            newY = Math.min(canvasDimensions.height - template.margins.bottom - element.height, element.y + step);
            break;
          case 'ArrowLeft':
            newX = Math.max(template.margins.left, element.x - step);
            break;
          case 'ArrowRight':
            newX = Math.min(canvasDimensions.width - template.margins.right - element.width, element.x + step);
            break;
        }
        
        updateElement(selectedElement, { x: newX, y: newY });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, editingElement, deleteElement, undo, redo, saveTemplate, zoomIn, zoomOut, zoomToFit, template, canvasDimensions, updateElement]);

  // Export template
  const exportTemplate = useCallback(() => {
    const dataStr = JSON.stringify(template, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template.name.replace(/\s+/g, '_')}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  }, [template]);

  // Preview template with sample data
  // Set template as personal default (new user-level preference)
  const setPersonalDefault = useCallback(async () => {
    if (!template.id || template.id.startsWith('template-')) {
      toast.error('Please save the template before setting it as default');
      return;
    }

    setIsSettingDefault(true);
    try {
      // Set as user's personal default template
      await userPreferencesService.updateWorkflowPreferences({
        defaultPreApprovalTemplateId: isMyPersonalDefault ? undefined : template.id
      });

      setIsMyPersonalDefault(!isMyPersonalDefault);

      if (!isMyPersonalDefault) {
        toast.success('✓ Set as your personal default template');
      } else {
        toast.info('Personal default template removed');
      }
    } catch (error) {
      logger.error('Error setting personal default template:', error);
      toast.error('Failed to set personal default');
    } finally {
      setIsSettingDefault(false);
    }
  }, [template.id, isMyPersonalDefault]);

  // Combined save and set as default
  const saveAndSetDefault = useCallback(async () => {
    // First save the template
    await saveTemplate();

    // Then set as personal default if saved successfully
    if (!template.id.startsWith('template-')) {
      await setPersonalDefault();
    }
  }, [saveTemplate, setPersonalDefault, template.id]);

  // Legacy organization-wide default (deprecated)
  const setAsDefault = useCallback(async () => {
    if (!template.id || template.id.startsWith('template-')) {
      toast.error('Please save the template before setting it as default');
      return;
    }

    setIsSettingDefault(true);
    try {
      const { error } = await preApprovalTemplateService.setDefaultTemplate(template.id);

      if (error) {
        toast.error(`Failed to set as default: ${error.message}`);
      } else {
        toast.success('Template set as default successfully');
        // Reload template data from database to get the updated flags
        const { data: updatedTemplate, error: loadError } = await preApprovalTemplateService.getTemplateById(template.id);
        if (updatedTemplate && !loadError) {
          setTemplateData(updatedTemplate);
        }
      }
    } catch (error) {
      logger.error('Error setting default template:', error);
      toast.error('Failed to set template as default');
    } finally {
      setIsSettingDefault(false);
    }
  }, [template.id]);

  const previewTemplate = useCallback(() => {
    // Create sample pre-approval data for preview
    const sampleData: PreApprovalData = {
      id: `preview-${  Date.now()}`,
      templateId: template.id,
      loanOfficerId: 'sample-officer',
      borrowerInfo: {
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@example.com',
        phone: '(555) 123-4567'
      },
      loanDetails: {
        loanAmount: 350000,
        purchasePrice: 400000,
        downPaymentPercent: 12.5,
        downPaymentAmount: 50000,
        loanType: 'conventional'
      },
      propertyInfo: {
        propertyType: 'single-family',
        occupancy: 'primary',
        address: '123 Main Street',
        city: 'Anytown',
        state: 'ST',
        zipCode: '12345'
      },
      customFields: {},
      expirationDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      calculations: {
        ltv: 87.5
      },
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      downloadVisualPreApprovalPDF(template, sampleData);
      toast.success('Preview PDF generated with sample data');
    } catch (error) {
      logger.error('Failed to generate preview:', error);
      toast.error('Failed to generate preview PDF');
    }
  }, [template]);

  // Render text with variables
  const renderTextWithVariables = (text: string) => {
    const parts = text.split(/({{[^}]+}})/g);
    return parts.map((part, index) => {
      if (part.match(/^{{[^}]+}}$/)) {
        return (
          <span key={index} className="inline-block bg-blue-100 text-blue-700 px-1 rounded font-medium" style={{ fontSize: 'inherit' }}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const selectedElementData = selectedElement 
    ? template.elements.find(el => el.id === selectedElement)
    : null;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Hidden file input for image uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={processImageUpload}
        style={{ display: 'none' }}
      />

      {/* Modern Two-Row Toolbar */}
      <div className="bg-white border-b shadow-sm">
        {/* Row 1: Primary Controls */}
        <div className="flex items-center justify-between px-3 py-1.5 gap-2 min-h-[40px] border-b border-gray-100">
          {/* Left Section */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Back to Templates Button */}
            {onBackToGallery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackToGallery}
                className="h-8 px-2 hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}

            {/* Template Name Input (Compact) */}
            <div className="flex items-center gap-1.5">
              {templateData?.is_locked && (
                <Lock className="w-3.5 h-3.5 text-gray-400" title={`Corporate template - ${isAdmin ? 'You can edit this template' : 'Read-only'}`} />
              )}
              <Input
                value={template.name}
                onChange={(e) => updateTemplate({ name: e.target.value })}
                className="w-48 h-8 text-sm font-medium"
                placeholder="Template name"
                disabled={templateData?.is_locked && !isAdmin}
              />
            </div>

            {/* Compact Badges */}
            <div className="flex items-center gap-1.5">
              <Badge variant="secondary" className="h-5 text-xs px-1.5">
                {template.elements.length} {template.elements.length === 1 ? 'element' : 'elements'}
              </Badge>
              {templateData?.is_corporate_default && (
                <Badge className="h-5 text-xs px-1.5 bg-green-50 text-green-700 border-green-200">
                  Corporate
                </Badge>
              )}
              {isMyPersonalDefault && (
                <Badge className="h-5 text-xs px-1.5 bg-blue-50 text-blue-700 border-blue-200">
                  My Default
                </Badge>
              )}
            </div>
          </div>

          {/* Center Section - History & Zoom */}
          <div className="flex items-center gap-2">
            {/* Undo/Redo Group */}
            <div className="flex items-center gap-0.5 px-1 py-0.5 rounded-md border border-gray-200 bg-gray-50/50">
              <Button
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={undo}
                disabled={historyIndex === 0}
                title="Undo (Ctrl+Z)"
              >
                <Undo className="w-3.5 h-3.5" />
              </Button>
              <div className="h-4 w-px bg-gray-300" />
              <Button
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={redo}
                disabled={historyIndex === history.length - 1}
                title="Redo (Ctrl+Y)"
              >
                <Redo className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Compact Zoom Controls */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={zoomOut}
                disabled={!canZoomOut}
                title="Zoom Out"
              >
                <Minimize2 className="w-3.5 h-3.5" />
              </Button>
              <Select value={zoom.toString()} onValueChange={(v) => setZoom(parseInt(v))}>
                <SelectTrigger className="h-7 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25%</SelectItem>
                  <SelectItem value="50">50%</SelectItem>
                  <SelectItem value="75">75%</SelectItem>
                  <SelectItem value="100">100%</SelectItem>
                  <SelectItem value="125">125%</SelectItem>
                  <SelectItem value="150">150%</SelectItem>
                  <SelectItem value="200">200%</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={zoomIn}
                disabled={!canZoomIn}
                title="Zoom In"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Right Section - Primary Actions */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-8 px-3" onClick={previewTemplate}>
              <Eye className="w-4 h-4 mr-1.5" />
              Preview
            </Button>

            {/* Actions Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  disabled={isSaving || (templateData?.is_locked && !isAdmin)}
                  className="h-8 px-4 bg-[#183C2E] hover:bg-[#153628] shadow-sm"
                >
                  {isSaving ? (
                    <>
                      <div className="w-3.5 h-3.5 mr-1.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1.5" />
                      Save
                      <ChevronDown className="w-3.5 h-3.5 ml-1" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={saveTemplate}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Template
                  <span className="ml-auto text-xs text-gray-500">Ctrl+S</span>
                </DropdownMenuItem>
                {!template.id.startsWith('template-') && (
                  <>
                    <DropdownMenuItem onClick={setPersonalDefault}>
                      <Star className={`w-4 h-4 mr-2 ${isMyPersonalDefault ? "fill-current text-blue-600" : ""}`} />
                      {isMyPersonalDefault ? "Remove as Default" : "Set as My Default"}
                      <span className="ml-auto text-xs text-gray-500">Ctrl+Shift+D</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={saveAndSetDefault}>
                      <Star className="w-4 h-4 mr-2" />
                      Save & Set as Default
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={exportTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Export as JSON
                </DropdownMenuItem>
                {templateData?.is_locked && !isAdmin && (
                  <DropdownMenuItem onClick={duplicateTemplate}>
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate Template
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Row 2: Secondary Info */}
        <div className="flex items-center justify-between px-3 py-1 gap-2 bg-gray-50/30 min-h-[32px]">
          {/* Template Info */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {!template.id.startsWith('template-') && (
              <>
                <span className="font-mono">ID: {template.id.substring(0, 8)}</span>
                <div className="h-3 w-px bg-gray-300" />
              </>
            )}
            {templateData?.updated_at && (
              <span>
                Last updated {new Date(templateData.updated_at).toLocaleDateString()} at{' '}
                {new Date(templateData.updated_at).toLocaleTimeString()}
              </span>
            )}
          </div>

          {/* Secondary Actions */}
          <div className="flex items-center gap-1">
            <KeyboardShortcuts />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-7 px-2 text-xs text-gray-600 hover:text-gray-900">
                  <Settings className="w-3.5 h-3.5 mr-1" />
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={zoomToFit}>
                  <Maximize2 className="w-4 h-4 mr-2" />
                  Zoom to Fit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowGrid(!showGrid)}>
                  <Grid3x3 className="w-4 h-4 mr-2" />
                  {showGrid ? "Hide Grid" : "Show Grid"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPreventOverlap(!preventOverlap)}>
                  <Shield className="w-4 h-4 mr-2" />
                  {preventOverlap ? "Allow Overlap" : "Prevent Overlap"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Read-only message for locked templates */}
      {templateData?.is_locked && !isAdmin && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
          <div className="flex items-center gap-2 text-sm text-yellow-800">
            <Lock className="w-4 h-4" />
            <span>This is a read-only corporate template. Click "Duplicate Template" to create your own editable copy.</span>
          </div>
        </div>
      )}

      <div className="flex-1 flex">
        {/* Left Sidebar */}
        <TemplateEditorSidebar
          onAddElement={addElement}
          showGrid={showGrid}
          onShowGridChange={setShowGrid}
          preventOverlap={preventOverlap}
          onPreventOverlapChange={setPreventOverlap}
          isAdmin={isAdmin}
          isCorporateDefault={templateData?.is_corporate_default}
          onCorporateDefaultChange={(checked) => {
            const updatedData = {
              ...(templateData || { id: template.id }),
              is_corporate_default: checked,
              is_locked: checked // Auto-lock when making corporate
            } as PreApprovalTemplate;

            setTemplateData(updatedData);
            toast.info('Remember to save the template to apply corporate default setting');
          }}
          isLocked={templateData?.is_locked}
          onLockedChange={(checked) => {
            setTemplateData({
              ...(templateData || { id: template.id }),
              is_locked: checked
            } as PreApprovalTemplate);
          }}
        />

        {/* Canvas */}
        <div className="flex-1 p-6 overflow-auto relative">
          <div
            className="flex justify-center"
            style={{
              minWidth: `${canvasDimensions.width * (zoom / 100)}px`,
              minHeight: `${canvasDimensions.height * (zoom / 100)}px`
            }}
          >
            <div
              ref={canvasRef}
              className="relative bg-white shadow-lg border select-none"
              style={{
                width: canvasDimensions.width,
                height: canvasDimensions.height,
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top left',
                transition: 'transform 0.2s ease-out'
              }}
              onClick={(e) => {
 handleCanvasClick(e); setContextMenu(null); 
}}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onContextMenu={(e) => {
                // Dismiss context menu when right-clicking empty canvas area
                if (e.target === canvasRef.current) {
                  e.preventDefault();
                  setContextMenu(null);
                }
              }}
            >
              {/* Grid overlay */}
              {showGrid && (
                <svg
                  className="absolute inset-0 pointer-events-none"
                  width={canvasDimensions.width}
                  height={canvasDimensions.height}
                >
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              )}

              {/* Margin guides */}
              <div
                className="absolute border border-dashed border-gray-300 pointer-events-none"
                style={{
                  left: template.margins.left,
                  top: template.margins.top,
                  right: template.margins.right,
                  bottom: template.margins.bottom,
                  width: canvasDimensions.width - template.margins.left - template.margins.right,
                  height: canvasDimensions.height - template.margins.top - template.margins.bottom
                }}
              />

              {/* Elements */}
              {template.elements.map((element, index) => {
                const isSelected = selectedElement === element.id;
                const isEditing = editingElement === element.id;

                return (
                  <div
                    key={element.id}
                    className={`absolute ${!isEditing ? 'cursor-move' : ''} ${
                      isSelected ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-300'
                    }`}
                    style={{
                      left: element.x,
                      top: element.y,
                      width: element.width,
                      height: element.height,
                      zIndex: index + 1
                    }}
                    onMouseDown={(e) => !isEditing && handleMouseDown(e, element.id)}
                    onDoubleClick={(e) => element.type === 'text' && handleDoubleClick(e, element.id)}
                    onContextMenu={(e) => {
                      // Allow native context menu when editing text (for copy/paste/spellcheck)
                      if (isEditing) {
return;
}
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedElement(element.id);
                      setContextMenu({ elementId: element.id, x: e.clientX, y: e.clientY });
                    }}
                  >
                    {/* Element content */}
                    <div
                      className="w-full h-full overflow-hidden"
                      style={{
                        fontSize: element.styles.fontSize,
                        fontFamily: element.styles.fontFamily,
                        fontWeight: element.styles.fontWeight,
                        fontStyle: element.styles.fontStyle,
                        textDecoration: element.styles.textDecoration,
                        textAlign: element.styles.textAlign,
                        color: element.styles.color,
                        backgroundColor: element.styles.backgroundColor,
                        padding: element.styles.padding,
                        borderRadius: element.styles.borderRadius,
                        lineHeight: element.styles.lineHeight
                      }}
                    >
                      {element.type === 'text' && !isEditing && (
                        <div className="whitespace-pre-wrap">
                          {renderTextWithVariables(element.content || 'Click to edit')}
                        </div>
                      )}
                      
                      {element.type === 'text' && isEditing && (
                        <textarea
                          ref={editInputRef}
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onBlur={() => {
                            // Use a timeout to allow toolbar interactions to set the flag
                            setTimeout(() => {
                              if (!isInteractingWithToolbar && !isVariablePopoverOpen) {
                                saveEditingText();
                              }
                            }, 100);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              cancelEditingText();
                            } else if (e.key === 'Enter' && e.ctrlKey) {
                              saveEditingText();
                            }
                            e.stopPropagation();
                          }}
                          className="w-full h-full resize-none border-0 outline-none bg-transparent"
                          style={{
                            fontSize: element.styles.fontSize,
                            fontFamily: element.styles.fontFamily,
                            fontWeight: element.styles.fontWeight,
                            fontStyle: element.styles.fontStyle,
                            textDecoration: element.styles.textDecoration,
                            textAlign: element.styles.textAlign,
                            color: element.styles.color,
                            lineHeight: element.styles.lineHeight,
                            padding: 0
                          }}
                          placeholder="Enter text here..."
                        />
                      )}
                      
                      {element.type === 'divider' && (
                        <div 
                          className="w-full"
                          style={{ 
                            height: 2,
                            backgroundColor: element.styles.backgroundColor || '#000000'
                          }}
                        />
                      )}
                      
                      {element.type === 'image' && (
                        <div className="w-full h-full flex items-center justify-center">
                          {element.imageUrl ? (
                            <img
                              src={element.imageUrl}
                              alt="Uploaded image"
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <button
                              onClick={() => handleImageUpload(element.id)}
                              className="w-full h-full flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                              <Upload className="w-8 h-8 mb-2" />
                              <span className="text-sm">Click to upload</span>
                            </button>
                          )}
                        </div>
                      )}

                      {element.type === 'signature' && (
                        <div className="w-full h-full flex items-center justify-center p-2">
                          <div className="text-center">
                            <Pen className="w-6 h-6 mx-auto mb-1 text-gray-400" />
                            <p className="text-xs text-gray-500 font-medium">Loan Officer Signature</p>
                            <p className="text-[10px] text-gray-400">{'{{loanOfficerSignature}}'}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Resize handles */}
                    {isSelected && !isEditing && (
                      <>
                        {resizeHandles.map((handle) => (
                          <div
                            key={handle.position}
                            className={`absolute w-2 h-2 bg-blue-500 border border-white ${handle.cursor}`}
                            style={{
                              ...getHandlePosition(handle.position),
                              cursor: handle.cursor
                            }}
                            onMouseDown={(e) => handleMouseDown(e, element.id, handle.position)}
                          />
                        ))}
                      </>
                    )}
                  </div>
                );
              })}
              
              {/* Editing toolbar - rendered outside elements */}
              {editingElement && (() => {
                const element = template.elements.find(el => el.id === editingElement);
                if (!element) {
return null;
}
                
                // Calculate toolbar position
                const toolbarY = element.y < 50 ? element.y + element.height + 10 : element.y - 50;
                
                return (
                  <div
                    data-editing-toolbar
                    className="absolute bg-white border rounded-lg shadow-lg p-2 z-50"
                    style={{
                      left: element.x,
                      top: toolbarY
                    }}
                    onMouseEnter={() => setIsInteractingWithToolbar(true)}
                    onMouseLeave={() => setIsInteractingWithToolbar(false)}
                    onMouseDown={(e) => {
                      // Prevent toolbar interactions from blurring textarea
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {/* Insert Variable menu */}
                      <Popover open={isVariablePopoverOpen} onOpenChange={setIsVariablePopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                          >
                            <Variable className="w-4 h-4 mr-1" />
                            Insert Variable
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 max-h-[500px] overflow-y-auto p-3" align="start">
                          <div className="space-y-4">
                            {Object.entries(variableCategories).map(([category, variables]) => (
                              <div key={category}>
                                <h4 className="font-medium text-sm text-gray-900 mb-2">{category}</h4>
                                <div className="space-y-0.5">
                                  {variables.map((variable) => (
                                    <button
                                      key={variable.id}
                                      onClick={() => {
                                        insertVariable(variable.id);
                                        setIsVariablePopoverOpen(false);
                                        // Keep focus on textarea
                                        editInputRef.current?.focus();
                                      }}
                                      className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded text-sm transition-colors"
                                    >
                                      <div className="font-medium text-gray-700">{variable.label}</div>
                                      <div className="text-xs text-gray-500">{`{{${variable.id}}}`}</div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>

                      <Separator orientation="vertical" className="h-6" />

                      {/* Action buttons */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          saveEditingText();
                        }}
                        className="h-8 px-3"
                        title="Save changes (Ctrl+Enter)"
                      >
                        Done
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          cancelEditingText();
                        }}
                        className="h-8 px-3 text-gray-500"
                        title="Cancel changes (Escape)"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Element Layer Context Menu */}
        {contextMenu && (() => {
          const elementIndex = template.elements.findIndex(el => el.id === contextMenu.elementId);
          if (elementIndex === -1) {
return null;
}
          const isFirst = elementIndex === 0;
          const isLast = elementIndex === template.elements.length - 1;

          const handleAction = (action: () => TemplateElement[]) => {
            updateTemplate({ elements: action() });
            setContextMenu(null);
          };

          const menuItemClass = "flex w-full items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 cursor-default select-none";

          // Clamp menu position to viewport to prevent off-screen rendering
          const menuWidth = 220;
          const menuHeight = 260;
          const clampedX = Math.min(contextMenu.x, window.innerWidth - menuWidth);
          const clampedY = Math.min(contextMenu.y, window.innerHeight - menuHeight);

          return (
            <>
              <div
                className="fixed inset-0 z-[100]"
                onClick={() => setContextMenu(null)}
                onContextMenu={(e) => {
 e.preventDefault(); setContextMenu(null); 
}}
              />
              <div
                className="fixed z-[101] min-w-[200px] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
                style={{ left: clampedX, top: clampedY }}
              >
                <button
                  className={menuItemClass}
                  disabled={isLast}
                  onClick={() => handleAction(() => bringToFront(template.elements, contextMenu.elementId))}
                >
                  <ChevronsUp className="w-4 h-4 mr-2" />
                  Bring to Front
                  <span className="ml-auto text-xs tracking-widest opacity-60 pl-4">Ctrl+Shift+]</span>
                </button>
                <button
                  className={menuItemClass}
                  disabled={isLast}
                  onClick={() => handleAction(() => bringForward(template.elements, contextMenu.elementId))}
                >
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Bring Forward
                  <span className="ml-auto text-xs tracking-widest opacity-60 pl-4">Ctrl+]</span>
                </button>
                <button
                  className={menuItemClass}
                  disabled={isFirst}
                  onClick={() => handleAction(() => sendBackward(template.elements, contextMenu.elementId))}
                >
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Send Backward
                  <span className="ml-auto text-xs tracking-widest opacity-60 pl-4">Ctrl+[</span>
                </button>
                <button
                  className={menuItemClass}
                  disabled={isFirst}
                  onClick={() => handleAction(() => sendToBack(template.elements, contextMenu.elementId))}
                >
                  <ChevronsDown className="w-4 h-4 mr-2" />
                  Send to Back
                  <span className="ml-auto text-xs tracking-widest opacity-60 pl-4">Ctrl+Shift+[</span>
                </button>
                <div className="-mx-1 my-1 h-px bg-muted" />
                <button
                  className={menuItemClass}
                  onClick={() => {
                    const el = template.elements[elementIndex];
                    const newElement: TemplateElement = {
                      ...el,
                      id: `element-${Date.now()}`,
                      x: el.x + 20,
                      y: el.y + 20
                    };
                    updateTemplate({ elements: [...template.elements, newElement] });
                    setSelectedElement(newElement.id);
                    setContextMenu(null);
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </button>
                <button
                  className={`${menuItemClass} text-destructive hover:text-destructive`}
                  onClick={() => {
                    deleteElement(contextMenu.elementId);
                    setContextMenu(null);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                  <span className="ml-auto text-xs tracking-widest opacity-60 pl-4">Del</span>
                </button>
              </div>
            </>
          );
        })()}

        {/* Properties Panel */}
        <div className="w-80 bg-white border-l p-4 overflow-auto">
          <Tabs defaultValue="element" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="element">Element</TabsTrigger>
              <TabsTrigger value="page">Page</TabsTrigger>
            </TabsList>
            
            <TabsContent value="element" className="space-y-4">
              {selectedElementData ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span>Element Properties</span>
                        <Badge variant="outline">{selectedElementData.type}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Position and Size */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">X Position</Label>
                          <Input
                            type="number"
                            value={Math.round(selectedElementData.x)}
                            onChange={(e) => updateElement(selectedElement!, { x: parseInt(e.target.value) })}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Y Position</Label>
                          <Input
                            type="number"
                            value={Math.round(selectedElementData.y)}
                            onChange={(e) => updateElement(selectedElement!, { y: parseInt(e.target.value) })}
                            className="h-8"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Width</Label>
                          <Input
                            type="number"
                            value={Math.round(selectedElementData.width)}
                            onChange={(e) => updateElement(selectedElement!, { width: parseInt(e.target.value) })}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Height</Label>
                          <Input
                            type="number"
                            value={Math.round(selectedElementData.height)}
                            onChange={(e) => updateElement(selectedElement!, { height: parseInt(e.target.value) })}
                            className="h-8"
                          />
                        </div>
                      </div>

                      {/* Image-specific controls */}
                      {selectedElementData.type === 'image' && selectedElementData.imageUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleImageUpload(selectedElement!)}
                          className="w-full"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Replace Image
                        </Button>
                      )}
                      
                      <Separator />

                      {/* Layer Order Controls */}
                      <div>
                        <Label className="text-xs mb-1.5 block">Layer Order</Label>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 flex-1"
                            onClick={() => updateTemplate({ elements: sendToBack(template.elements, selectedElement!) })}
                            disabled={template.elements.findIndex(el => el.id === selectedElement) === 0}
                            title="Send to Back (Ctrl+Shift+[)"
                          >
                            <ChevronsDown className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 flex-1"
                            onClick={() => updateTemplate({ elements: sendBackward(template.elements, selectedElement!) })}
                            disabled={template.elements.findIndex(el => el.id === selectedElement) === 0}
                            title="Send Backward (Ctrl+[)"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 flex-1"
                            onClick={() => updateTemplate({ elements: bringForward(template.elements, selectedElement!) })}
                            disabled={template.elements.findIndex(el => el.id === selectedElement) === template.elements.length - 1}
                            title="Bring Forward (Ctrl+])"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 flex-1"
                            onClick={() => updateTemplate({ elements: bringToFront(template.elements, selectedElement!) })}
                            disabled={template.elements.findIndex(el => el.id === selectedElement) === template.elements.length - 1}
                            title="Bring to Front (Ctrl+Shift+])"
                          >
                            <ChevronsUp className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Layer {template.elements.findIndex(el => el.id === selectedElement) + 1} of {template.elements.length}
                        </p>
                      </div>

                      <Separator />

                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteElement(selectedElement!)}
                        className="w-full"
                      >
                        Delete Element
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Text styling - only for text elements */}
                  {selectedElementData.type === 'text' && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Text Styling</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <Label className="text-xs">Font Size</Label>
                          <Input
                            type="number"
                            value={selectedElementData.styles.fontSize}
                            onChange={(e) => updateElement(selectedElement!, {
                              styles: { ...selectedElementData.styles, fontSize: parseInt(e.target.value) }
                            })}
                            className="h-8"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs">Font Family</Label>
                          <Select
                            value={selectedElementData.styles.fontFamily}
                            onValueChange={(value) => updateElement(selectedElement!, {
                              styles: { ...selectedElementData.styles, fontFamily: value }
                            })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Arial, sans-serif">Arial</SelectItem>
                              <SelectItem value="Times New Roman, serif">Times New Roman</SelectItem>
                              <SelectItem value="Georgia, serif">Georgia</SelectItem>
                              <SelectItem value="Helvetica, sans-serif">Helvetica</SelectItem>
                              <SelectItem value="Courier New, monospace">Courier New</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="flex gap-1">
                          <Button
                            variant={selectedElementData.styles.fontWeight === 'bold' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateElement(selectedElement!, {
                              styles: { 
                                ...selectedElementData.styles, 
                                fontWeight: selectedElementData.styles.fontWeight === 'bold' ? 'normal' : 'bold'
                              }
                            })}
                            className="h-8 px-2"
                          >
                            <Bold className="w-4 h-4" />
                          </Button>
                          <Button
                            variant={selectedElementData.styles.fontStyle === 'italic' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateElement(selectedElement!, {
                              styles: { 
                                ...selectedElementData.styles, 
                                fontStyle: selectedElementData.styles.fontStyle === 'italic' ? 'normal' : 'italic'
                              }
                            })}
                            className="h-8 px-2"
                          >
                            <Italic className="w-4 h-4" />
                          </Button>
                          <Button
                            variant={selectedElementData.styles.textDecoration === 'underline' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateElement(selectedElement!, {
                              styles: { 
                                ...selectedElementData.styles, 
                                textDecoration: selectedElementData.styles.textDecoration === 'underline' ? 'none' : 'underline'
                              }
                            })}
                            className="h-8 px-2"
                          >
                            <Underline className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="flex gap-1">
                          <Button
                            variant={selectedElementData.styles.textAlign === 'left' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateElement(selectedElement!, {
                              styles: { ...selectedElementData.styles, textAlign: 'left' }
                            })}
                            className="h-8 px-2"
                          >
                            <AlignLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            variant={selectedElementData.styles.textAlign === 'center' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateElement(selectedElement!, {
                              styles: { ...selectedElementData.styles, textAlign: 'center' }
                            })}
                            className="h-8 px-2"
                          >
                            <AlignCenter className="w-4 h-4" />
                          </Button>
                          <Button
                            variant={selectedElementData.styles.textAlign === 'right' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateElement(selectedElement!, {
                              styles: { ...selectedElementData.styles, textAlign: 'right' }
                            })}
                            className="h-8 px-2"
                          >
                            <AlignRight className="w-4 h-4" />
                          </Button>
                          <Button
                            variant={selectedElementData.styles.textAlign === 'justify' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateElement(selectedElement!, {
                              styles: { ...selectedElementData.styles, textAlign: 'justify' }
                            })}
                            className="h-8 px-2"
                          >
                            <AlignJustify className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div>
                          <Label className="text-xs">Text Color</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={selectedElementData.styles.color}
                              onChange={(e) => updateElement(selectedElement!, {
                                styles: { ...selectedElementData.styles, color: e.target.value }
                              })}
                              className="w-12 h-8 border rounded cursor-pointer"
                            />
                            <Input
                              value={selectedElementData.styles.color}
                              onChange={(e) => updateElement(selectedElement!, {
                                styles: { ...selectedElementData.styles, color: e.target.value }
                              })}
                              className="flex-1 h-8"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Line Height</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={selectedElementData.styles.lineHeight || 1.5}
                            onChange={(e) => updateElement(selectedElement!, {
                              styles: { ...selectedElementData.styles, lineHeight: parseFloat(e.target.value) }
                            })}
                            className="h-8"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <Maximize2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-sm">Select an element to edit its properties</p>
                  <p className="text-xs mt-2">Double-click text to edit inline</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="page" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Page Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Page Size</Label>
                    <Select
                      value={template.pageSize}
                      onValueChange={(value: 'letter' | 'a4') => updateTemplate({ pageSize: value })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="letter">Letter (8.5" × 11")</SelectItem>
                        <SelectItem value="a4">A4 (210mm × 297mm)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Margins (px)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Top</Label>
                        <Input
                          type="number"
                          value={template.margins.top}
                          onChange={(e) => updateTemplate({
                            margins: { ...template.margins, top: parseInt(e.target.value) }
                          })}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Bottom</Label>
                        <Input
                          type="number"
                          value={template.margins.bottom}
                          onChange={(e) => updateTemplate({
                            margins: { ...template.margins, bottom: parseInt(e.target.value) }
                          })}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Left</Label>
                        <Input
                          type="number"
                          value={template.margins.left}
                          onChange={(e) => updateTemplate({
                            margins: { ...template.margins, left: parseInt(e.target.value) }
                          })}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Right</Label>
                        <Input
                          type="number"
                          value={template.margins.right}
                          onChange={(e) => updateTemplate({
                            margins: { ...template.margins, right: parseInt(e.target.value) }
                          })}
                          className="h-8"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Template Management Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-1">
                    <Settings className="w-4 h-4" />
                    Template Management
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Personal Default Status */}
                  {!template.id.startsWith('template-') && (
                    <div>
                      <Label className="text-xs mb-2 block text-gray-600">Personal Default Template</Label>
                      {isMyPersonalDefault ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-md">
                            <Star className="w-4 h-4 text-blue-600 fill-current" />
                            <span className="text-sm text-blue-700 font-medium">This is your default template</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={setPersonalDefault}
                            disabled={isSettingDefault}
                            className="w-full"
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Remove as Default
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={setPersonalDefault}
                          disabled={isSettingDefault || template.id.startsWith('template-')}
                          className="w-full hover:bg-blue-50"
                        >
                          <Star className="w-4 h-4 mr-1" />
                          Set as My Default
                        </Button>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Use Ctrl+Shift+D to toggle default status
                      </p>
                    </div>
                  )}

                  {/* Organization Default (Legacy) */}
                  {templateData?.is_organization_default && (
                    <div className="pt-2 border-t">
                      <div className="flex items-center gap-2 p-2 bg-orange-50 rounded-md">
                        <Star className="w-4 h-4 text-orange-600 fill-current" />
                        <span className="text-sm text-orange-700 font-medium">Organization Default</span>
                      </div>
                    </div>
                  )}

                  {/* Corporate Default */}
                  {templateData?.is_corporate_default && (
                    <div className="pt-2 border-t">
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded-md">
                        <Lock className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-700 font-medium">Corporate Default (Locked)</span>
                      </div>
                    </div>
                  )}

                  {/* Template Information */}
                  {!template.id.startsWith('template-') && (
                    <div className="pt-3 border-t space-y-2">
                      <div>
                        <Label className="text-xs text-gray-500">Template ID</Label>
                        <p className="text-xs font-mono text-gray-700 mt-1">
                          {template.id.substring(0, 8)}...
                        </p>
                      </div>

                      {templateData?.last_updated_by && templateData?.updated_at && (
                        <div>
                          <Label className="text-xs text-gray-500">Last Modified</Label>
                          <p className="text-xs text-gray-700 mt-1">
                            {new Date(templateData.updated_at).toLocaleDateString()} at{' '}
                            {new Date(templateData.updated_at).toLocaleTimeString()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Available Variables</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {Object.entries(variableCategories).map(([category, variables]) => (
                      <div key={category}>
                        <h4 className="text-xs font-medium text-gray-500 mb-1">{category}</h4>
                        <div className="space-y-1">
                          {variables.map((variable) => (
                            <div
                              key={variable.id}
                              className="flex items-center justify-between py-1 px-2 hover:bg-gray-50 rounded"
                            >
                              <span className="text-sm">{variable.label}</span>
                              <code className="text-xs bg-gray-100 px-1 rounded">
                                {`{{${variable.id}}}`}
                              </code>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

// Helper function to get resize handle positions
function getHandlePosition(position: string): React.CSSProperties {
  const size = -4; // Half of handle size (8px)
  
  switch (position) {
    case 'nw': return { top: size, left: size };
    case 'n': return { top: size, left: '50%', transform: 'translateX(-50%)' };
    case 'ne': return { top: size, right: size };
    case 'e': return { top: '50%', right: size, transform: 'translateY(-50%)' };
    case 'se': return { bottom: size, right: size };
    case 's': return { bottom: size, left: '50%', transform: 'translateX(-50%)' };
    case 'sw': return { bottom: size, left: size };
    case 'w': return { top: '50%', left: size, transform: 'translateY(-50%)' };
    default: return {};
  }
}